import { inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import DOMPurify, { RemovedAttribute, RemovedElement } from 'dompurify';

export interface SanitizationResult {
  clean: string;
  isMalicious: boolean;
}

const config = {
  USE_PROFILES: { svg: true, svgFilters: true },
  ADD_TAGS: [
    'use',
    'metadata',
    'rdf:RDF',
    'rdf:Description',
    'cc:Work',
    'dc:format',
    'dc:type',
    'dc:title',
    'dc:creator',
    'cc:Agent',
    'cc:License',
    'cc:permits',
    'cc:requires',
    'cc:prohibits',
  ], // Allow additional SVG-specific tags
  ADD_ATTR: [
    'xlink:href',
    'fill',
    'stroke',
    'stroke-width',
    'width',
    'height',
    'viewBox',
    'd',
    'xmlns',
    'xmlns:rdf',
    'xmlns:cc',
    'xmlns:dc',
    'xmlns:xlink',
    'version',
    'style',
    'rdf:about',
    'rdf:resource',
    'id',
  ], // Allow required SVG attributes
  FORBID_TAGS: ['script', 'iframe', 'foreignObject'], // Explicitly forbid potentially harmful tags
  FORBID_ATTR: ['onload', 'onclick', 'onmouseover'], // Forbid event handlers
  ALLOWED_URI_REGEXP:
    /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i, // Allow more URI schemes
  KEEP_CONTENT: true, // Preserve node content if a tag is removed
};

@Injectable({
  providedIn: 'root',
})
export class CustomSanitizerService {
  private readonly defaultSanitizer = inject(DomSanitizer);

  constructor() {
    DOMPurify.setConfig(config);
  }

  sanitizeSvgString(dirty: string): SanitizationResult {
    const clean = DOMPurify.sanitize(dirty, config);
    const isClean = this.isClean(DOMPurify.removed);
    return {
      clean,
      isMalicious: !isClean,
    };
  }

  private isClean(removed: (RemovedElement | RemovedAttribute)[]): boolean {
    return removed.every(element => {
      const removedElement = element as RemovedElement;
      const removedAttribute = element as RemovedAttribute;
      if (removedElement.element) {
        const nodeName = removedElement.element.nodeName.toUpperCase();
        if (nodeName !== 'BODY') {
          return false;
        }
      } else if (removedAttribute.attribute) {
        return false;
      }
      return true;
    });
  }
}
